import ModalFooterButtons from "@/components/molecules/modals/Footer/ModalFooterButtons";
import { NestedPublicVenueSerializer } from "@app-contexts/public/features/event-detail/data/types/events";
import HStack from "@ui-kit/layout/Stack/HStack.web";
import VStack from "@ui-kit/layout/Stack/VStack.web";
import { enums } from "@shared/constants";
import useWindowSize from "@shared/hooks/useWindowSize";
import { PublicMembershipGroupSerializer } from "@shared/modules/memberships/types";
import CartTimer from "@ui-application/data-display/CartTimer/CartTimer.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Modal from "@ui-kit/overlays/Modal/Modal.web";
import ReadMore from "@ui-kit/typography/ReadMore/ReadMore.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";
import { useMemo } from "react";
import MembershipTypesContainer from "@shared/modules/memberships/components/MembershipTypesContainer/MembershipTypesContainer";

type MembershipModalType = {
  isOpen: boolean;
  onClose: () => void;
  selectedAttractionMembership: PublicMembershipGroupSerializer;
  venue: NestedPublicVenueSerializer;
  dynamicHoldSlug?: string;
  purchaseLimits?: {
    min: number;
    max: number;
  };
  itemCount?: number;
};

export default function MembershipModal({
  isOpen,
  onClose,
  selectedAttractionMembership,
  venue,
  dynamicHoldSlug = undefined,
  purchaseLimits = undefined,
  itemCount = 0,
}: MembershipModalType) {
  const { t } = useTranslation("purchases");
  const { width: windowWidth } = useWindowSize();
  const displaySingleCol = useMemo(
    () => windowWidth && windowWidth < 1400,
    [windowWidth]
  );

  const readMoreComponent = (
    <Flex direction="column" pb="lg" width="100%">
      <Text fontWeight="bold">{selectedAttractionMembership.name}</Text>
      {selectedAttractionMembership.description && (
        <ReadMore
          openReadMoreText={t("Read More")}
          closeReadMoreText={t("Show Less")}
          descriptionHtml={selectedAttractionMembership.description}
        />
      )}
    </Flex>
  );

  const membershipListComponent = (
    <Flex direction="column" width="100%" gap="md">
      {selectedAttractionMembership.membership_levels?.length ? (
        selectedAttractionMembership.membership_levels?.map((level) => {
          return (
            <Flex
              key={level.id}
              position="relative"
              width="100%"
              direction="column"
            >
              <MembershipTypesContainer
                isLoading={false}
                membershipLevel={level}
                venue={venue}
                selectedAttractionMembership={selectedAttractionMembership}
                hasAllInPricing={Boolean(venue.all_in_pricing_is_enabled)}
                isModal
                purchaseLimits={purchaseLimits}
                dynamicHoldSlug={dynamicHoldSlug}
              />
            </Flex>
          );
        })
      ) : (
        <Flex width="100%" justifyContent="flex-start">
          <Text fontSize={{ base: "md", sm: "3xl" }} fontWeight="bold">
            {t("No membership levels found.")}
          </Text>
        </Flex>
      )}
    </Flex>
  );

  return (
    // specify static values for i18n extraction since it can't interpolate variables
    // t("Memberships Selection")
    // t("Passes Selection")
    <Modal
      isCentered
      scrollBehavior="inside"
      size={displaySingleCol ? "2xl" : "6xl"}
      isOpen={isOpen}
      onClose={onClose}
    >
      <Modal.Header alignItems="center">
        {t(
          selectedAttractionMembership.verbiage &&
            t(
              `${
                enums.MEMBERSHIP_GROUP_VERBIAGE_MAP_BY_ID[
                  selectedAttractionMembership.verbiage
                ]?.name
              } Selection`,
              {
                type: t(
                  enums.MEMBERSHIP_GROUP_VERBIAGE_MAP_BY_ID[
                    selectedAttractionMembership.verbiage
                  ]?.name,
                  { ns: "constants" }
                ),
              }
            )
        )}
      </Modal.Header>
      <Modal.Body p="none">
        <CartTimer.Banner />
        <Flex direction="column" padding="xl">
          {displaySingleCol ? (
            <VStack width="100%">
              {readMoreComponent} {membershipListComponent}
            </VStack>
          ) : (
            <HStack alignItems="flex-start" width="100%" height="100%">
              <VStack width="40%" height="100%">
                <HStack width="100%">
                  {readMoreComponent}
                  <Box minWidth="element.3xl" />
                </HStack>
              </VStack>
              <VStack width="60%" height="100%">
                {membershipListComponent}
              </VStack>
            </HStack>
          )}
        </Flex>
      </Modal.Body>
      <Modal.Footer>
        <ModalFooterButtons
          venue={venue}
          onClose={onClose}
          isDynamicHold={!!dynamicHoldSlug}
          hasAdditionalItems={(itemCount ?? 0) > 1}
        />
      </Modal.Footer>
    </Modal>
  );
}
